import { useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { BrandPanel } from "@/components/auth/BrandPanel";
import { LanguageSwitcher } from "@/components/layout/LanguageSwitcher";
import { APP_VERSION_LABEL, PRODUCT_NAME, LOGO_SRC } from "@/lib/brand";
import { Mail, ArrowLeft, Loader2, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiErrorMessage(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <BrandPanel />
      <div className="flex-1 flex items-center justify-center px-5 sm:px-8 py-8">
        <div className="w-full max-w-md" data-testid="forgot-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="bg-white border border-slate-200 rounded-xl p-1.5 shadow-sm">
              <img src={LOGO_SRC} alt="REASON" className="h-9 w-9 object-contain" />
            </div>
            <span className="font-extrabold text-lg text-slate-900">{PRODUCT_NAME}</span>
          </div>

          {sent ? (
            <div data-testid="forgot-success">
              <div className="h-14 w-14 rounded-full bg-[#E3F3F2] flex items-center justify-center mb-5">
                <MailCheck className="h-7 w-7 text-[#0E8C84]" />
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900">{t("auth.checkEmail", "Check your email")}</h1>
              <p className="text-slate-500 text-sm mt-2">
                {t("auth.resetSent", "If an account exists for")} <b>{email}</b>,{" "}
                {t("auth.resetSent2", "we've sent a password reset link. It is valid for 1 hour.")}
              </p>
            </div>
          ) : (
            <form onSubmit={submit}>
              <h1 className="text-3xl font-extrabold text-slate-900">{t("auth.forgotTitle", "Forgot Password?")}</h1>
              <p className="text-slate-500 text-sm mt-2 mb-7">
                {t("auth.forgotSub", "Enter your email and we'll send you a secure reset link.")}
              </p>
              {error && (
                <div className="mb-5 text-sm rounded-xl bg-red-50 text-red-700 px-4 py-3 border border-red-100" data-testid="forgot-error">
                  {error}
                </div>
              )}
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t("auth.email", "Email Address")}</label>
              <div className="relative mb-6">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
                <input
                  type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl border border-slate-300 pl-11 pr-3.5 py-3 text-sm outline-none focus:ring-2 focus:ring-[#118D89]/30 focus:border-[#118D89]"
                  placeholder="you@company.com" data-testid="forgot-email"
                />
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full bg-[#0E8C84] hover:bg-[#0c7c74] text-white rounded-xl py-3.5 text-sm font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
                data-testid="forgot-submit"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {t("auth.sendReset", "Send reset link")}
              </button>
            </form>
          )}

          <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold text-[#118D89] hover:underline mt-7" data-testid="forgot-back">
            <ArrowLeft className="h-4 w-4" /> {t("auth.backToLogin", "Back to sign in")}
          </Link>

          <div className="flex items-center justify-between mt-8">
            <LanguageSwitcher />
            <span className="text-xs font-semibold text-slate-400">{APP_VERSION_LABEL}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
